import type { BookingTransactionDataInput } from '@drycut/graphql';

import { BookingCheckoutButton, PreBookingCheckoutButton } from '@drycut/controlled-components';
import { Para, Stack } from '@drycut/ui-components';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { computeBookingPrice } from '../../computeBookingPrice';
import { NewCouponInsert } from '../../CouponInsert';
import { useCheckoutStore } from '../checkoutStore';
import StepIndicator from '../StepIndicator';

export function Step4(): React.ReactElement {
  const { control } = useFormContext();
  const selectedStore = useCheckoutStore((state) => state.selectedStore);
  const manual = useCheckoutStore((state) => state.manual);
  const scheduled = useCheckoutStore((state) => state.scheduled);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const selectedCoupon = useCheckoutStore((state) => state.selectedCoupon);
  const setSelectedCoupon = useCheckoutStore((state) => state.setSelectedCoupon);

  const kidCustomer = useWatch({ name: 'kidCustomer' });
  const customerName = useWatch({ name: 'name' });
  const phoneNumber = useWatch({ name: 'phone' });
  const silentBooking = useWatch({ name: 'silentBooking' });

  const { isValid } = useFormState({ control });

  const isDisabled = !isValid || !customerName || !phoneNumber || !selectedStore;

  const computedPrice = computeBookingPrice({
    tax: selectedSchedule ? selectedStore?.preBookingTax || 0 : 0,
    service: selectedStore?.service,
    coupon: selectedCoupon,
    kidCustomer,
  });

  const bookingData: BookingTransactionDataInput = {
    manual,
    customerName,
    phoneNumber,
    silentBooking,
    kidCustomer,
    couponCode: selectedCoupon?.code,
    storeSlug: selectedStore?.slug || '',
  };

  return (
    <>
      <StepIndicator index={4} disabled={isDisabled}>
        Checkout
      </StepIndicator>

      <Stack gap={2} className={isDisabled ? 'opacity-30' : ''}>
        <div
          className={twJoin(
            'flex items-center justify-center w-full grow',
            isDisabled ? 'opacity-50' : 'opacity-100',
          )}
        >
          <Stack direction="row" gap={2} className="align-start pb-3 w-full">
            <Para size="xl" as="span">
              Preis:
            </Para>
            <Para size="xl" as="span" weight="medium">
              {computedPrice} CHF
            </Para>
          </Stack>
        </div>

        {scheduled ? (
          <PreBookingCheckoutButton
            disabled={isDisabled}
            amount={computedPrice}
            data={{ ...bookingData, bookTime: selectedSchedule?.toISO() as string }}
          />
        ) : (
          <BookingCheckoutButton disabled={isDisabled} amount={computedPrice} data={bookingData} />
        )}

        <NewCouponInsert disabled={isDisabled} onValidate={(coupon) => setSelectedCoupon(coupon)} />
      </Stack>
    </>
  );
}
